import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, clearSuperModeToken, getSuperModeToken, setSuperModeToken } from "../lib/api";

/**
 * V27 — Super Mode context.
 *
 *  - Boots admin dashboard in "Normal Mode" (active=false).
 *  - Holds the short-lived super-mode JWT (sessionStorage), polls expiry, runs an
 *    inactivity timer (15 min), and exposes lock/unlock/heartbeat helpers.
 *  - Auto-locks on browser tab inactivity > 15 min and on window blur > 15 min.
 *  - Auto-locks on any 403 `super_mode_required` axios error (so an expired
 *    token surfaces a clean re-auth prompt instead of broken pages).
 */
const SuperModeContext = createContext(null);

const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;  // refresh token every 5 min while active

export function SuperModeProvider({ children }) {
    const [status, setStatus] = useState({
        isSuperAdmin: false,
        pinSet: false,
        active: false,
        expiresAt: null,
        sessionTtlMinutes: 15,
        recoveryEmail: "",
        loading: true,
    });

    const expireTimerRef = useRef(null);
    const heartbeatTimerRef = useRef(null);
    const inactivityTimerRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    // --------- Server status fetch ---------
    const refreshStatus = useCallback(async () => {
        try {
            const res = await api.get("/admin/super-mode/status");
            setStatus((s) => ({
                ...s,
                isSuperAdmin: !!res.data.is_super_admin,
                pinSet: !!res.data.pin_set,
                active: !!res.data.super_mode_active,
                expiresAt: res.data.super_mode_expires_at || null,
                sessionTtlMinutes: res.data.session_ttl_minutes || 15,
                recoveryEmail: res.data.recovery_email || "",
                loading: false,
            }));
            return res.data;
        } catch (err) {
            // 403/401 → not super admin (regular admin). Hide the feature entirely.
            setStatus((s) => ({
                ...s,
                isSuperAdmin: false,
                pinSet: false,
                active: false,
                loading: false,
            }));
            return null;
        }
    }, []);

    // --------- Timers ---------
    const clearTimers = useCallback(() => {
        if (expireTimerRef.current) { clearTimeout(expireTimerRef.current); expireTimerRef.current = null; }
        if (heartbeatTimerRef.current) { clearInterval(heartbeatTimerRef.current); heartbeatTimerRef.current = null; }
        if (inactivityTimerRef.current) { clearInterval(inactivityTimerRef.current); inactivityTimerRef.current = null; }
    }, []);

    const lock = useCallback(async (reason = "manual") => {
        clearTimers();
        clearSuperModeToken();
        // Best-effort audit (don't block UX on failure)
        try { await api.post("/admin/super-mode/lock", { reason }); } catch { /* ignore */ }
        setStatus((s) => ({ ...s, active: false, expiresAt: null }));
    }, [clearTimers]);

    const heartbeat = useCallback(async () => {
        const tok = getSuperModeToken();
        if (!tok) return;
        try {
            const res = await api.post("/admin/super-mode/heartbeat");
            if (res.data?.super_mode_token) {
                setSuperModeToken(res.data.super_mode_token);
                setStatus((s) => ({ ...s, expiresAt: res.data.expires_at || s.expiresAt }));
            }
        } catch (err) {
            // 401 → expired
            if (err?.response?.status === 401) {
                await lock("expired");
            }
        }
    }, [lock]);

    const armTimers = useCallback((expiresAtIso) => {
        clearTimers();
        if (!expiresAtIso) return;
        const expMs = new Date(expiresAtIso).getTime() - Date.now();
        if (expMs > 0) {
            expireTimerRef.current = setTimeout(() => lock("expired"), expMs);
        }
        heartbeatTimerRef.current = setInterval(() => {
            if (Date.now() - lastActivityRef.current < INACTIVITY_TIMEOUT_MS) heartbeat();
        }, HEARTBEAT_INTERVAL_MS);
        inactivityTimerRef.current = setInterval(() => {
            if (Date.now() - lastActivityRef.current >= INACTIVITY_TIMEOUT_MS) lock("inactivity");
        }, 30 * 1000);
    }, [clearTimers, heartbeat, lock]);

    // --------- Initial load ---------
    useEffect(() => { refreshStatus(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    // --------- Activity tracker ---------
    useEffect(() => {
        if (!status.active) return undefined;
        const onAct = () => { lastActivityRef.current = Date.now(); };
        const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
        events.forEach((e) => window.addEventListener(e, onAct, { passive: true }));
        return () => events.forEach((e) => window.removeEventListener(e, onAct));
    }, [status.active]);

    // --------- Token expiry timers ---------
    useEffect(() => {
        if (status.active && status.expiresAt) armTimers(status.expiresAt);
        else clearTimers();
        return clearTimers;
    }, [status.active, status.expiresAt, armTimers, clearTimers]);

    // --------- Cross-tab logout ---------
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === "at_token" && !e.newValue) {
                clearSuperModeToken();
                setStatus((s) => ({ ...s, active: false }));
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    // --------- Public actions ---------
    const unlock = useCallback(async (mpin) => {
        const res = await api.post("/admin/super-mode/unlock", { mpin });
        setSuperModeToken(res.data.super_mode_token);
        setStatus((s) => ({
            ...s,
            active: true,
            expiresAt: res.data.expires_at,
            sessionTtlMinutes: res.data.session_ttl_minutes || 15,
        }));
        lastActivityRef.current = Date.now();
        return res.data;
    }, []);

    const requestSetupOtp = useCallback(async () => {
        const res = await api.post("/admin/super-mode/request-setup-otp");
        return res.data;
    }, []);

    const setupMpin = useCallback(async (otp, newMpin) => {
        const res = await api.post("/admin/super-mode/setup-mpin", { otp, new_mpin: newMpin });
        await refreshStatus();
        return res.data;
    }, [refreshStatus]);

    const requestResetOtp = useCallback(async () => {
        const res = await api.post("/admin/super-mode/request-reset-otp");
        return res.data;
    }, []);

    const resetMpin = useCallback(async (otp, newMpin) => {
        const res = await api.post("/admin/super-mode/reset-mpin", { otp, new_mpin: newMpin });
        await refreshStatus();
        return res.data;
    }, [refreshStatus]);

    const ctx = useMemo(() => ({
        ...status,
        refreshStatus,
        unlock,
        lock,
        requestSetupOtp,
        setupMpin,
        requestResetOtp,
        resetMpin,
    }), [status, refreshStatus, unlock, lock, requestSetupOtp, setupMpin, requestResetOtp, resetMpin]);

    return <SuperModeContext.Provider value={ctx}>{children}</SuperModeContext.Provider>;
}

export function useSuperMode() {
    const ctx = useContext(SuperModeContext);
    if (!ctx) throw new Error("useSuperMode must be used inside <SuperModeProvider>");
    return ctx;
}
